/**
 * AutosuggestField React component
 * 
 * @author    Maurizio Tidei
 * @copyright © 2018 contexagon GmbH
 */


import React from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import deburr from 'lodash/deburr';
import {withStyles} from '@material-ui/core/styles';
import Popper from '@material-ui/core/Popper';


/**
 * AutosuggestField is a React component for entering text with an autosuggest dropdown
 *
 * @author Maurizio Tidei
 */
class AutosuggestField extends React.Component {

    state = {
        single: '',
        popper: '',
        suggestions: [],
    };

    handleSuggestionsFetchRequested = ({value}) => {
        this.setState({
            suggestions: this.getSuggestions(value),
        });
    };

    handleSuggestionsClearRequested = () => {
        this.setState({
            suggestions: [],
        });
    };

    handleChange = name => (event, {newValue}) => {
        this.setState({
            [name]: newValue,
        });
    };

    handleKeyDown = event => {

        if (event.keyCode === 13) {

            this.props.onEnterKey(this.state.popper);
            this.setState({'popper': ''});
        }
    };

    renderInputComponent = (inputProps) => {
        const {
            classes, inputRef = () => {
            }, ref, ...other
        } = inputProps;

        return (
            <TextField
                InputProps={{
                    inputRef: node => {
                        ref(node);
                        inputRef(node);
                    },
                    classes: {
                        input: classes.input,
                    },
                }}
                {...other}
            />
        );
    };

    renderSuggestion = (suggestion, {query, isHighlighted}) => {
        const matches = match(suggestion, query); // TODO: match() matches only at the beginning of a word
        const parts = parse(suggestion, matches);

        return (
            <MenuItem selected={isHighlighted} component="div">
                <div>
                    {parts.map((part, index) => {
                        return part.highlight ? (
                            <span key={String(index)} style={{fontWeight: 501}}>
              {part.text}
            </span>
                        ) : (
                            <strong key={String(index)} style={{fontWeight: 300}}>
                                {part.text}
                            </strong>
                        );
                    })}
                </div>
            </MenuItem>
        );
    };

    getSuggestions = (value) => {
        const inputValue = deburr(value.trim()).toLowerCase();
        const inputLength = inputValue.length;
        let count = 0;

        return inputLength === 0
            ? []
            : this.props.suggestions.filter(suggestion => {
                const keep =
                    count < 7 && (suggestion.toLowerCase().includes(inputValue) || value === "*");

                if (keep) {
                    count += 1;
                }

                return keep;
            });
    };

    getSuggestionValue = (suggestion) => {
        return suggestion;
    };


    render() {

        // console.log("AutosuggestField.render(), this.state:", this.state, " this.props:", this.props);

        const {classes} = this.props;

        const autosuggestProps = {
            renderInputComponent: this.renderInputComponent,
            suggestions: this.state.suggestions,
            onSuggestionsFetchRequested: this.handleSuggestionsFetchRequested,
            onSuggestionsClearRequested: this.handleSuggestionsClearRequested,
            getSuggestionValue: this.getSuggestionValue,
            renderSuggestion: this.renderSuggestion,
        };

        return (
            <Autosuggest
                {...autosuggestProps}
                inputProps={{
                    classes,
                    placeholder: this.props.placeholder,
                    value: this.state.popper,
                    onChange: this.handleChange('popper'),
                    margin: "dense",
                    inputRef: node => {
                        this.popperNode = node;
                    },
                    InputLabelProps: {
                        shrink: true
                    },
                    onKeyDown: this.handleKeyDown
                    //InputProps: {
                    //    disableUnderline: true
                    //}
                }}
                theme={{
                    suggestionsList: classes.suggestionsList,
                    suggestion: classes.suggestion,
                    container: classes.container
                }}
                renderSuggestionsContainer={options => (
                    <Popper anchorEl={this.popperNode} open={Boolean(options.children)}>
                        <Paper
                            square
                            {...options.containerProps}
                            style={{width: this.popperNode ? this.popperNode.clientWidth : null}}
                        >
                            {options.children}
                        </Paper>
                    </Popper>
                )}
            />
        );
    }
}

AutosuggestField.propTypes = {
    placeholder: PropTypes.string.isRequired,
    suggestions: PropTypes.array,
    onEnterKey: PropTypes.func
};

const styles = theme => ({
    root: {
        height: 250,
        flexGrow: 1,
        display: "inline"
    },
    container: {
        position: 'relative',
        display: 'inline'
    },
    suggestionsContainerOpen: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing.unit,
        left: 0,
        right: 0,
    },
    suggestion: {
        display: 'block',
    },
    suggestionsList: {
        margin: 0,
        padding: 0,
        listStyleType: 'none',
    }
});

export default withStyles(styles)(AutosuggestField)